import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, Pie } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import { FormControl, MenuItem, Select } from '@mui/material';
import { QcsDropdownMenu } from '../../common/basic/QcsDropdownMenu';
import { AnalyticsGroupDto } from '@api/models/analytics/AnalyticsGroupDto';
import { ANALYTICS_AXIS_COLOR, ANALYTICS_PIE_BORDER, getSeriesColor, paletteFor } from './AnalyticsColors';
import { baseBarOptions, basePieOptions } from './AnalyticsChartDefaults';
import {
  AxisSelect,
  ChartArea,
  ChartContainer,
  ChartFloatingControls,
  ChartWithAxes,
  PieSelectsRow,
  XAxisSelectArea,
  YAxisSelectArea
} from './AnalyticsChartStyles';
import { EmptyState } from './AnalyticsSharedStyles';
import { ColumnMetaDto } from '@api/models/analytics/ColumnMetaDto';
import { formatCellValue, isNumericColumn } from '../analyticsColumns';
import { ChartKind } from './AnalyticsResultToolbar';
import { GroupByOption } from '../analyticsDomainConfig';
import { buildGroupByDropdownItems } from './buildGroupByDropdownItems';
import { formatGroupLabel } from '../analyticsDateUtils';
import { sortGroupsByValueDesc } from './sortGroupsByValue';

interface Props {
  groups: AnalyticsGroupDto[];
  columns: ColumnMetaDto[];
  yKey: string;
  chartKind: ChartKind;
  groupBy: string;
  groupByOptions: GroupByOption[];
  onGroupByChange: (next: string) => void;
  onYKeyChange: (next: string) => void;
  onChartKindChange: (next: ChartKind) => void;
}

export const AnalyticsGroupsChart: FC<Props> = ({
  groups,
  columns,
  yKey,
  chartKind,
  groupBy,
  groupByOptions,
  onGroupByChange,
  onYKeyChange,
  onChartKindChange,
}) => {
  const { t, i18n } = useTranslation();

  const numericColumns = useMemo(() => columns.filter((c) => isNumericColumn(c.type)), [columns]);

  const yCol = numericColumns.find((c) => c.key === yKey);
  const selectYKey = yCol?.key ?? '';

  // Entity groupings show highest value first; time groupings keep chronological order.
  const groupByGroup = groupByOptions.find((o) => o.value === groupBy)?.group;
  const sortByValue = groupByGroup !== 'time' && !!selectYKey;
  const displayGroups = useMemo(
    () => (sortByValue ? sortGroupsByValueDesc(groups, selectYKey) : groups),
    [groups, selectYKey, sortByValue],
  );

  const labels = useMemo(
    () => displayGroups.map((g, idx) => formatGroupLabel(g.groupKey, groupBy, `#${idx + 1}`, i18n)),
    [displayGroups, groupBy, i18n],
  );

  const rawValues = useMemo(
    () =>
      displayGroups.map((g) => {
        const v = selectYKey ? g.values?.[selectYKey] : null;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      }),
    [displayGroups, selectYKey],
  );

  const yLabel = useMemo(() => (yCol ? t(yCol.labelKey) : ''), [yCol, t]);

  const barData = useMemo(() => {
    const color = getSeriesColor(0);
    return {
      labels,
      datasets: [
        {
          label: yLabel,
          data: rawValues,
          backgroundColor: color,
          borderColor: color,
          borderRadius: 6,
          maxBarThickness: 36,
        },
      ],
    };
  }, [labels, rawValues, yLabel]);

  const pieData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: yLabel,
          data: rawValues,
          backgroundColor: paletteFor(labels),
          borderColor: ANALYTICS_PIE_BORDER,
          borderWidth: 2,
        },
      ],
    }),
    [labels, rawValues, yLabel],
  );

  const yColType = yCol?.type;

  // Explorer chart overlays: typed tooltips (formatCellValue), bold title font, and X-axis ticks visible.
  const barOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      ...baseBarOptions,
      plugins: {
        ...baseBarOptions.plugins,
        tooltip: {
          ...baseBarOptions.plugins!.tooltip,
          titleFont: { weight: 600 },
          callbacks: {
            label: (ctx) => `${yLabel}: ${yColType ? formatCellValue(Number(ctx.parsed.y), yColType, i18n) : String(ctx.parsed.y)}`,
          },
        },
      },
      scales: {
        ...baseBarOptions.scales,
        x: { ...baseBarOptions.scales!.x, ticks: { color: ANALYTICS_AXIS_COLOR } },
        y: {
          ...baseBarOptions.scales!.y,
          ticks: {
            ...baseBarOptions.scales!.y!.ticks,
            // Duration metrics: axis ticks as hh:mm:ss instead of raw seconds.
            ...(yColType === 'DURATION_SEC' ? { callback: (v: string | number) => formatCellValue(Number(v), 'DURATION_SEC', i18n) } : {}),
          },
        },
      },
    }),
    [yLabel, yColType, i18n],
  );

  const pieOptions: ChartOptions<'pie'> = useMemo(
    () => ({
      ...basePieOptions,
      plugins: {
        ...basePieOptions.plugins,
        legend: { position: 'right', labels: { boxWidth: 12, boxHeight: 12 } },
        tooltip: {
          ...basePieOptions.plugins!.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.label}: ${yColType ? formatCellValue(Number(ctx.parsed), yColType, i18n) : String(ctx.parsed)}`,
          },
        },
      },
    }),
    [yColType, i18n],
  );

  if (numericColumns.length === 0) {
    return (
      <EmptyState style={{ width: '100%', height: '300px' }}>
        <svg
          width={28}
          height={28}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ opacity: 0.4 }}
        >
          <rect x="3" y="12" width="4" height="9" rx="1" />
          <rect x="10" y="7" width="4" height="14" rx="1" />
          <rect x="17" y="3" width="4" height="18" rx="1" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t('analytics.chart.noNumeric')}</span>
      </EmptyState>
    );
  }

  const isPie = chartKind === 'pie';

  const xSelect = (
    <AxisSelect>
      <FormControl size="small">
        <QcsDropdownMenu
          value={groupBy}
          onChange={(e) => onGroupByChange(e.target.value as string)}
          items={buildGroupByDropdownItems(groupByOptions, t)}
        />
      </FormControl>
    </AxisSelect>
  );

  const ySelect = (
    <AxisSelect>
      <FormControl size="small">
        <Select value={selectYKey} onChange={(e) => onYKeyChange(e.target.value)}>
          {numericColumns.map((c) => (
            <MenuItem key={c.key} value={c.key}>
              {t(c.labelKey)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </AxisSelect>
  );

  return (
    <ChartWithAxes>
      {/* Y axis — label + select along left edge */}
      {!isPie && (
        <YAxisSelectArea>
          <span className="axis-label">{t('analytics.chart.yAxisLabel')}</span>
          {ySelect}
        </YAxisSelectArea>
      )}

      {/* Chart + floating chart-kind toggle */}
      <ChartArea style={isPie ? { gridColumn: '1 / -1' } : undefined}>
        <ChartFloatingControls>
          <FormControl size="small">
            <Select value={chartKind} onChange={(e) => onChartKindChange(e.target.value as ChartKind)}>
              <MenuItem value="bar">{t('analytics.chart.kind.bar')}</MenuItem>
              <MenuItem value="pie">{t('analytics.chart.kind.pie')}</MenuItem>
            </Select>
          </FormControl>
        </ChartFloatingControls>

        <ChartContainer>{isPie ? <Pie data={pieData} options={pieOptions} /> : <Bar data={barData} options={barOptions} />}</ChartContainer>
      </ChartArea>

      {/* X axis — label + select centered below chart */}
      {!isPie && (
        <XAxisSelectArea>
          <span className="axis-label">{t('analytics.filter.groupByLabel')}</span>
          {xSelect}
        </XAxisSelectArea>
      )}

      {/* Pie: both selects in a row below */}
      {isPie && (
        <PieSelectsRow>
          <div className="axis-group">
            <span className="axis-label">{t('analytics.filter.groupByLabel')}</span>
            {xSelect}
          </div>
          <div className="axis-group">
            <span className="axis-label">{t('analytics.chart.yAxisLabel')}</span>
            {ySelect}
          </div>
        </PieSelectsRow>
      )}
    </ChartWithAxes>
  );
};
